import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { parseJwt } from '../utils/jwt-utils';
import { Logger } from '../utils/logger';
import axios from './../utils/axiosInstance';
import {
  APPLICATION_MODES,
  useApplicationViewState,
} from './ApplicationViewStateContext';
import { CmsDomain, OwnershipTokenContext } from './types';

interface OwnershipClaimRequest {
  domain: string;
  type: string;
  path: string;
}

interface OwnershipClaimContextProps {
  selectedOwnershipDomain: CmsDomain | null;
  setSelectedOwnershipDomain: Dispatch<SetStateAction<CmsDomain | null>>;
  ownershipRequestToken: string | null;
  ownershipAccessToken: string | null;
  errorMessage: string | null;
  setErrorMessage: Dispatch<SetStateAction<string | null>>;
  fetchOwnershipRequestToken: (domain: string) => void;
  requestOwnershipClaim: (request: OwnershipClaimRequest) => void;
  resetOwnershipAccessToken: () => void;
  ownershipAccessRequest: OwnershipClaimRequest | null;
  setOwnershipAccessRequest: Dispatch<
    SetStateAction<OwnershipClaimRequest | null>
  >;
  ownershipTokenContext: OwnershipTokenContext | null;
  setAccessTokenAndContext: (accessToken: string) => void;
}

const OwnershipClaimContext = createContext<OwnershipClaimContextProps | null>(
  null,
);

const OwnershipClaimProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [selectedOwnershipDomain, setSelectedOwnershipDomain] =
    useState<CmsDomain | null>(null);
  const [ownershipRequestToken, setOwnershipRequestToken] = useState<
    string | null
  >(null);
  const [ownershipAccessRequest, setOwnershipAccessRequest] =
    useState<OwnershipClaimRequest | null>(null);
  const [ownershipAccessToken, _setOwnershipAccessToken] = useState<
    string | null
  >(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [ownershipTokenContext, _setOwnershipTokenContext] =
    useState<OwnershipTokenContext | null>(null);

  const fetchOwnershipRequestToken = async (domain: string) => {
    setErrorMessage('');
    Logger.log('fetchOwnershipRequestToken for', { domain });
    try {
      const response = (
        await axios.post('/api/accessibe/ownership-claim/verification-token', {
          domain,
        })
      ).data;
      if (response.success && response.data?.verificationToken) {
        Logger.log('Ownership Request response', response);
        setOwnershipRequestToken(response.data.verificationToken);
      } else {
        setErrorMessage('Error in getting ownership request token');
      }
    } catch (error) {
      setErrorMessage('Error while getting ownership request token');
    }
  };

  const setAccessTokenAndContext = (accessToken: string) => {
    const { licenseId, domain, accountId } = parseJwt(accessToken);
    Logger.log('Setting Ownership Access Token', {
      licenseId,
      domain,
      accountId,
    });
    _setOwnershipAccessToken(accessToken);
    _setOwnershipTokenContext({ licenseId, domain, accountId });
    window.localStorage.setItem('ownership-claim-access-token', accessToken);
  };

  const requestOwnershipClaim = async (request: OwnershipClaimRequest) => {
    Logger.log('requestOwnershipClaim', request);
    try {
      const response = (
        await axios.post('/api/accessibe/ownership-claim/access-token', request)
      ).data;
      Logger.log('Ownership Claim response', response);
      if (response.success && response.data?.accessToken) {
        setAccessTokenAndContext(response.data.accessToken);
      } else {
        setErrorMessage('Error in requesting ownership claim');
      }
    } catch (error) {
      Logger.log('Error in requesting ownership claim', error);
      setErrorMessage('Error while requesting ownership claim');
    }
  };

  const resetOwnershipAccessToken = () => {
    setSelectedOwnershipDomain(null);
    setOwnershipRequestToken(null);
    setOwnershipAccessRequest(null);
    _setOwnershipAccessToken(null);
    _setOwnershipTokenContext(null);
    setApplicationMode(APPLICATION_MODES.DEFAULT);
    window.localStorage.removeItem('ownership-claim-access-token');
  };

  const { setApplicationMode } = useApplicationViewState();
  useEffect(() => {
    const accessToken = window.localStorage.getItem(
      'ownership-claim-access-token',
    );
    if (accessToken) {
      Logger.log('Loading Ownership Access Token from storage');
      setAccessTokenAndContext(accessToken);
      setApplicationMode(APPLICATION_MODES.LIMITED);
    }
  }, [setApplicationMode]);

  return (
    <OwnershipClaimContext.Provider
      value={{
        ownershipRequestToken,
        ownershipAccessToken,
        errorMessage,
        setErrorMessage,
        selectedOwnershipDomain,
        ownershipAccessRequest,
        setOwnershipAccessRequest,
        setSelectedOwnershipDomain,
        fetchOwnershipRequestToken,
        requestOwnershipClaim,
        resetOwnershipAccessToken,
        ownershipTokenContext,
        setAccessTokenAndContext,
      }}
    >
      {children}
    </OwnershipClaimContext.Provider>
  );
};

const useOwnershipClaim = (): OwnershipClaimContextProps => {
  const context = useContext(OwnershipClaimContext);
  if (context === null) {
    throw new Error(
      'useOwnershipClaim must be used within an OwnershipClaimProvider',
    );
  }
  return context;
};

export { OwnershipClaimProvider, useOwnershipClaim };
